import {eventBus} from "../../event-bus";
import {useCallback, useEffect, useRef, useState} from "react"
import {Typography, Pagination, Box, CircularProgress, MenuItem, Button, CardHeader} from "@mui/material"
import {useTranslation} from "react-i18next"
import i18n from "../../i18n/i18n"
import axiosInstance from "../../config/axios-config"
import TableContainerComp from "../../components/table-container-comp"
import {PageContainer, PaginationContainer} from "../../components/common/page-container"
import {SearchFilterContainer, SearchField, ItemsSelect} from "../../components/common/search-filter-container"
import ProductDetailsDialog from "../../dialog/product-details-dialog"
import {Refresh as RefreshIcon} from "@mui/icons-material";
import GlowingMessage from "../../errors/glowing-message";

const ProductsPharmacy = ({navOpen, tenantType}) => {
    const list_categories = ["MEDICINES", "MEDICAL_EQUIPMENT", "MEDICINAL_COSMETICS", "FOR_CHILDREN", "BABY_FOOD", "COSMETICS", "PSYCHOACTIVE", "CONTRACEPTIVES", "OTHER"]
    const [page, setPage] = useState(0)
    const [searchQuery, setSearchQuery] = useState("")
    const [category, setCategory] = useState("")
    const [filterCompany, setFilterCompany] = useState("")
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
    const [selectedProductForDetails, setSelectedProductForDetails] = useState(null)
    const {t} = useTranslation()

    const [rows, setProducts] = useState([])
    const [distributors, setDistributors] = useState([])
    const [totalPages, setTotalPages] = useState(0)
    const [loading, setLoading] = useState(true)
    const [message, setMessage] = useState("")
    const productRequest = useRef(null)


    const fetchProducts = useCallback(async (page, search, category, filterCompany) => {
        productRequest.current?.abort()
        const controller = new AbortController()
        productRequest.current = controller
        setLoading(true)
        try {
            const response = await axiosInstance.get("/api/v1/product_tenants/pharmacy/all", {
                signal: controller.signal,
                params: {
                    searchTerm: search?.length >= 3 ? search : null,
                    tenantName: filterCompany === t("Distributors") ? null : filterCompany,
                    category: category?.toUpperCase() === t("AllCategories").toUpperCase() ? null : category,
                    page: page,
                    size: 10,
                },
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })
            if (controller.signal.aborted) return
            const {content, totalPages} = response.data
            setProducts(content || [])
            // Retain known suppliers when filters or pagination return a smaller or empty result.
            setDistributors(previous => [...new Set([
                ...previous, ...(content || []).map(product => product.tenantName),
            ])].filter(Boolean))
            setTotalPages(totalPages)
        } catch (error) {
            if (controller.signal.aborted) return
            setMessage(error.response?.data?.message || "Failed to get products.")
            console.error("Error fetching products:", error)
        } finally {
            if (!controller.signal.aborted) setLoading(false)
        }
    }, [t])
    useEffect(() => {
        const handleRefresh = () => {
            fetchProducts(page, searchQuery, category, filterCompany).then(r => console.log(r))
        }
        eventBus.on("refreshProductsData", handleRefresh)

        return () => {
            // Clean up when component unmounts
            eventBus.off("refreshProductsData", handleRefresh)
        }
    }, [page, searchQuery, category, filterCompany, fetchProducts])

    useEffect(() => {
        productRequest.current?.abort()
        setLoading(true)
        // Defer initial loading so StrictMode replay can cancel the first scheduled task.
        const timeout = setTimeout(() => {
            fetchProducts(page, searchQuery, category, filterCompany)
        }, productRequest.current ? 500 : 0)
        return () => {
            clearTimeout(timeout)
            productRequest.current?.abort()
        }
    }, [page, searchQuery, category, filterCompany, fetchProducts])

    const handlePageChange = (_, newPage) => {
        setPage(newPage - 1)
    }

    // New handlers for product details dialog
    const handleProductNameClick = (product) => {
        setSelectedProductForDetails(product)
        setDetailsDialogOpen(true)
    }

    const handleDetailsDialogClose = () => {
        fetchProducts(page, searchQuery, category, filterCompany)
            .then(r => console.log(r))
        setDetailsDialogOpen(false)
    }

    const handleRefresh = () => {
        fetchProducts(page, searchQuery, category, filterCompany)
            .then(r => console.log(r))
    }

    return (
        <>
            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <PageContainer navOpen={navOpen}>
                <CardHeader
                    title={t("Products")}
                    subheader={t("ProductsTableDesc")}
                    action={
                        <Button variant="outlined" startIcon={<RefreshIcon/>} onClick={handleRefresh}
                                disabled={loading}>
                            {t("Refresh")}
                        </Button>
                    }
                />
                <SearchFilterContainer sx={{mr: 25}}>
                    <SearchField
                        placeholder={t("Search")}
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value)
                            setPage(0)
                        }}
                    />

                    <ItemsSelect
                        size="small"
                        select
                        value={filterCompany || t("Distributors")}
                        onChange={(e) => {
                            setFilterCompany(e.target.value)
                            setPage(0)
                        }}
                        variant="outlined"
                        SelectProps={{
                            MenuProps: {
                                disableScrollLock: true,
                                disablePortal: true,
                            },
                        }}
                    >
                        <MenuItem value={t("Distributors")}>{t("Distributors")}</MenuItem>
                        {distributors.map((distributor) => (
                            <MenuItem key={distributor} value={distributor}>
                                {distributor}
                            </MenuItem>
                        ))}
                    </ItemsSelect>

                    <ItemsSelect
                        size="small"
                        select
                        value={category || t("AllCategories")}
                        onChange={(e) => {
                            setCategory(e.target.value)
                            setPage(0)
                        }}
                        variant="outlined"
                        SelectProps={{
                            MenuProps: {
                                disableScrollLock: true,
                                disablePortal: true,
                            },
                        }}
                    >
                        <MenuItem value={t("AllCategories")}>{t("AllCategories")}</MenuItem>
                        {list_categories.map((category) => (
                            <MenuItem key={category} value={category}>
                                {t(category)}
                            </MenuItem>
                        ))}
                    </ItemsSelect>
                </SearchFilterContainer>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress/>
                    </Box>
                ) : rows.length === 0 ? (
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            padding: 4,
                            textAlign: "center",
                        }}
                    >
                        {t("ProductNotFound")}
                    </Typography>
                ) : (
                    <TableContainerComp
                        rows={rows}
                        isDistributor={true}
                        onProductNameClick={handleProductNameClick}
                    />
                )}

                <PaginationContainer>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={handlePageChange}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        siblingCount={1}
                    />
                </PaginationContainer>
                <ProductDetailsDialog
                    open={detailsDialogOpen}
                    onClose={handleDetailsDialogClose}
                    product={selectedProductForDetails}
                    tenantType={tenantType}
                />
            </PageContainer>
        </>
    )
}

export default ProductsPharmacy
